import ShadowContainerVertical from '@atoms/ShadowContainerVertical/ShadowContainerVertical';
import { ISubMenuItem } from '@models/IHeader';
import { ILink } from '@models/ILink';
import HeaderSubNavTab from '@molecules/HeaderSubNavTab/HeaderSubNavTab';
import updateDataLayer from '@utils/helpers/gtm';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface Props {
  readonly activeTab: ISubMenuItem[];
  readonly mainNavItemTitle: string;
  readonly mainNavItemLink?: ILink;
}

const MobileSecondLevel = ({
  activeTab,
  mainNavItemTitle,
  mainNavItemLink,
}: Props) => {
  const [activeSubNavContent, setActiveSubNavContent] =
    useState<ISubMenuItem | null>(null);
  const [activeNavItemIndex, setActiveNavItemIndex] = useState(0);
  const [subCategory, setSubCategory] = useState('');
  const isInIFrame = useIsInIFrame();

  useEffect(() => {
    const { navigationCards, links } = activeTab[0]!;
    setActiveSubNavContent({ navigationCards, links });
  }, [activeTab]);

  const handleLevelTwoItemClick = (index: number, itemName: string) => {
    setActiveNavItemIndex(index);
    setSubCategory(itemName);
    updateDataLayer({
      event: 'menuUsed',
      content_type: 'header_menu_level_2',
      main_category: mainNavItemTitle,
      sub_category: itemName,
      tertiary_category: undefined,
    });
  };

  return (
    <motion.div
      className="absolute top-0 right-0 bottom-0 left-0 overflow-y-auto"
      initial={{ x: '200%' }}
      animate={{ x: '100%' }}
      exit={{ x: '200%' }}
      transition={{
        delay: 0.15,
        duration: 1.1,
        ease: [0.74, 0, 0.19, 1.02],
      }}
      data-testid="CT-01-Header-Mobile-Second-Level"
    >
      <ShadowContainerVertical shadowColour="from-white" shadowHeight="h-6">
        <div
          className="flex px-5 pb-3 text-black-100 md:px-8 lgm:pl-32"
          data-testid={`CT-01-Header-Mobile-Second-Level-Title-${mainNavItemTitle}`}
        >
          {mainNavItemLink?.url ? (
            <a
              href={mainNavItemLink.url}
              target={isInIFrame ? '_parent' : mainNavItemLink.target}
            >
              <p className="h5">{mainNavItemTitle}</p>
            </a>
          ) : (
            <p className="h5">{mainNavItemTitle}</p>
          )}
        </div>

        <div className="w-full">
          <ul className="flex flex-row flex-nowrap overflow-x-auto bg-white pt-7 pr-5 pb-7 pl-5 md:pr-8 md:pl-8 lgm:pr-32 lgm:pl-32">
            {activeTab?.map(
              ({ link, navigationCards, links, title = '' }, index) => (
                <li
                  key={`level-2-${link?.name}`}
                  className={classNames(
                    'pr-5',
                    activeNavItemIndex === index
                      ? 'text-black-100'
                      : 'text-grey-500',
                    navigationCards?.length
                      ? 'cursor-default'
                      : 'cursor-pointer'
                  )}
                  data-testid="CT-01-Header-Mobile-Second-Level-Sub-Nav-Item"
                >
                  {link?.url && !navigationCards?.length ? (
                    <span className="flex flex-col items-center gap-2.5">
                      <a
                        href={link.url}
                        target={isInIFrame ? '_parent' : link.target}
                        onClick={() => {
                          setActiveNavItemIndex(index);
                          setActiveSubNavContent(null);
                        }}
                      >
                        <div
                          className="second-level-nav-title body-two whitespace-nowrap"
                          data-testid="CT-01-Header-Mobile-Second-Level-Sub-Nav-Item-Title"
                        >
                          {title}
                        </div>
                      </a>

                      {activeNavItemIndex === index && (
                        <div className="h-2 w-2 rounded-full bg-yellow" />
                      )}
                    </span>
                  ) : (
                    <div className="flex flex-col items-center gap-2.5">
                      <button
                        onClick={() => {
                          handleLevelTwoItemClick(index, title);
                          setActiveSubNavContent({
                            navigationCards,
                            links,
                          });
                        }}
                      >
                        <div
                          className="second-level-nav-title body-two whitespace-nowrap"
                          data-testid="CT-01-Header-Mobile-Second-Level-Sub-Nav-Item-Title"
                        >
                          {title}
                        </div>
                      </button>

                      {activeNavItemIndex === index && (
                        <div className="h-2 w-2 rounded-full bg-yellow" />
                      )}
                    </div>
                  )}
                </li>
              )
            )}
          </ul>

          {(!!activeSubNavContent?.navigationCards?.length ||
            !!activeSubNavContent?.links?.length) && (
            <HeaderSubNavTab
              activeTab={activeSubNavContent || {}}
              mainCategory={mainNavItemTitle}
              subCategory={subCategory}
              dataTestId="CT-01-Header-Sub-Nav-Tab"
            />
          )}
        </div>
      </ShadowContainerVertical>
    </motion.div>
  );
};

export default MobileSecondLevel;
